import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output
} from '@angular/core';
import {BarcodeScannerLivestreamComponent} from 'ngx-barcode-scanner';
import {StockOperation} from '../../models/enums';
import {QuaggaJSResultObject} from '@ericblade/quagga2';
import {EventEmitter} from '@angular/core';
import {UiToastService} from "../../shared/ui";
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-scanner',
  templateUrl: './mobile-scanner.component.html',
  styleUrls: ['./mobile-scanner.component.scss'],
  standalone: false
})
export class MobileScannerComponent implements OnInit {

  @ViewChild(BarcodeScannerLivestreamComponent) livestream: BarcodeScannerLivestreamComponent;
  @Input() op: StockOperation;
  @Input() sound: boolean;

  /**
   * Continuous batch mode: instead of proposing a code and waiting for the user
   * to tap "Accept", every confident read is emitted immediately via
   * `codeAccepted` and the camera keeps scanning. A short rescan delay debounces
   * duplicate reads of the same physical barcode.
   */
  @Input() continuous = false;
  @Input() rescanDelayMs = 1500;

  @Output() scannerReady = new EventEmitter<boolean>();
  @Output() productScanned = new EventEmitter<string>();
  @Output() codeAccepted = new EventEmitter<string>();

  proposedCode = "";
  canAccept = false;

  productQueue = [];
  beepSound: HTMLAudioElement = null;

  private scanBuffer: string[] = [];
  private readonly BUFFER_SIZE = 2;
  private canScan = true;
  private scanPauseMs = 3000;

  private hasStarted = false;

  protected scannerConfig = null;

  private readonly log: AppLogger;

  generateScannerConfig(): any {

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const config = {
      locate: false,
      numOfWorkers: 8,  // you can reduce this for mobile if needed
      inputStream: {
        type: 'LiveStream',
        constraints: {
          audio: false,
          width: {ideal: 1280},
          height: {ideal: 720},
          aspectRatio: {min: 1, max: 100, ideal: screenWidth / screenHeight},
          facingMode: "environment",
          focusMode: "continuous"
        },
        area: {
          top: "45%",
          right: "35%",
          left: "35%",
          bottom: "45%"
        },
        singleChannel: false,
        willReadFrequently: true
      },
      debug: {
        drawBoundingBox: true,
        showPattern: false,
        showFoundPattern: false,
        showCanvas: true,       // <-- hide canvas overlay
        showPatches: true,
        showSkeleton: false,
        showLabels: false,
        showPatchLabels: false,
        showRemainingPatchLabels: false,
        boxFromPatches: {
          showTransformed: false,
          showTransformedBox: false,
          showBB: false
        }
      }
    };

    this.log.info('Scanner config generated', { config });
    return config;
  }

  constructor(private appLogger: AppLoggerService, public toastService: UiToastService) {
    this.log = this.appLogger.createForClass(MobileScannerComponent);
  }

  ngOnInit(): void {
    // Create config eagerly (doesn't request camera).
    this.scannerConfig = this.generateScannerConfig();
    this.beepSound = new Audio();
    this.beepSound.src = '../../assets/sound/beep.wav';
    this.beepSound.load();
  }

  start(): void {
    if (this.hasStarted) return;

    try {
      this.livestream?.start();
      this.hasStarted = true;
    } catch {
      // ignore
    }
  }

  stop(): void {
    this.hasStarted = false;
    try {
      this.livestream?.stop();
    } catch {
      // ignore
    }
  }

  addToQueue(code: string): void {
    this.productQueue.push(code);
    this.log.debug('Barcode added to queue', { code });

    setTimeout(() => {
      this.shiftProduct();
    }, 1000);
  }

  shiftProduct(): void {
    const shiftedCode = this.productQueue.shift();
    this.productScanned.emit(shiftedCode);
    this.log.info('Barcode shifted from queue', { code: shiftedCode });
  }

  onValueChanges(result: QuaggaJSResultObject): void {

    if (!this.canScan) {
      return;
    }

    const code = result.codeResult.code;

    // Quick sanity: must be 13 digits
    if (!/^\d{13}$/.test(code)) {
      return;
    }

    // Rolling buffer
    this.scanBuffer.push(code);
    if (this.scanBuffer.length > this.BUFFER_SIZE) {
      this.scanBuffer.shift();
    }

    // If buffer full and all entries match → accept
    if (this.scanBuffer.length === this.BUFFER_SIZE &&
      this.scanBuffer.every(c => c === code)) {
      this.proposeToUser(code);
    }
  }

  private proposeToUser(code: string) {
    this.canScan = false;
    if (this.sound) {
      this.beepSound.play();
    }
    this.proposedCode = code;

    // reset buffer
    this.scanBuffer = [];

    if (this.continuous) {
      // Batch mode: accept immediately and keep scanning after a short debounce.
      this.log.info('Barcode auto-accepted (continuous)', { code });
      this.productScanned.emit(code);
      this.codeAccepted.emit(code);
      setTimeout(() => {
        this.canScan = true;
      }, this.rescanDelayMs);
      return;
    }

    this.canAccept = true;

    // pause before next scan
    setTimeout(() => {
      this.canScan = true;
    }, this.scanPauseMs);
  }

  acceptedCode(): void {
    this.log.info('Barcode accepted', { code: this.proposedCode });
    this.productScanned.emit(this.proposedCode);
    this.codeAccepted.emit(this.proposedCode);
    this.stop();
  }

  onStarted(started): void {
    this.scannerReady.emit(started);
    this.log.debug('Scanner state changed', { started });
  }
}
